import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../Config/Config';
import { doc, setDoc } from 'firebase/firestore';
import { Client, Storage, ID } from 'appwrite';
import { FaSpinner, FaCheckCircle, FaExclamationTriangle, FaTimes } from 'react-icons/fa';
import KYCForm from '../components/kyc/KYCForm';
import TaxResidencyForm from '../components/kyc/TaxResidencyForm';
import AMLForm from '../components/kyc/AMLForm';
import Spinner from '../components/Spinner';

const MultiStepForm = () => {
  const client = new Client()
    .setEndpoint('https://cloud.appwrite.io/v1')
    .setProject('683885060036d8e0f975');
  const storage = new Storage(client);

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [user, setUser] = useState(null);
  const [fileUploads, setFileUploads] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [overallProgress, setOverallProgress] = useState(0);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    // KYC Form
    proofOfAddress: '',
    proofOfAddressFile: null,
    governmentIdType: '',
    governmentIdFile: null,
    idNumber: '',
    financialStatementsFile: null,
    socialSecurityNumber: '',
    sourceOfFunds: '',
    otherSource: '',
    estimatedNetWorth: '',
    fullName: '',
    dateOfBirth: '',
    signatureFile: null,
    residentialAddress: '',
    city: '',
    state: '',
    postalCode: '',

    // Tax Residency Form
    isUSCitizen: '',
    taxResidencyCountries: '',
    tin: '',
    noTinReason: '',
    taxSignatureFile: null,

    // AML Form
    clientName: '',
    isPEP: '',
    highRiskIndustries: '',
    offshoreJurisdictions: '',
    investigatedForCrimes: '',
    explanation: '',
    amlSignatureFile: null,
    amlDate: ''
  });

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((authUser) => {
      if (authUser) {
        setUser(authUser);
      } else {
        navigate('/login');
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'file' ? files[0] : value
    }));
  };

  const handleCheckboxChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked ? value : ''
    }));
  };

  const uploadFile = async (file, fieldName) => {
    if (!file) return null;
    
    try {
      setFileUploads(prev => ({
        ...prev,
        [fieldName]: { status: 'uploading', progress: 0 }
      }));

      const permissions = user ? [
        `read("user:${user.uid}")`,
        `write("user:${user.uid}")`
      ] : [];

      const response = await storage.createFile(
        '684305690007db8ff8f4',
        ID.unique(),
        file,
        undefined,
        {
          onProgress: (progress) => {
            setUploadProgress(prev => ({
              ...prev,
              [fieldName]: progress
            }));
          }
        },
        permissions
      );

      const fileUrl = storage.getFileView('684305690007db8ff8f4', response.$id);
      
      setFileUploads(prev => ({
        ...prev,
        [fieldName]: { status: 'completed', url: fileUrl }
      }));

      return fileUrl;
    } catch (error) {
      console.error(`Error uploading ${fieldName}:`, error);
      setFileUploads(prev => ({
        ...prev,
        [fieldName]: { status: 'failed', error: error.message }
      }));
      throw error;
    }
  };

  const nextStep = () => {
    setStep(step + 1);
  };

  const prevStep = () => {
    setStep(step - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) return;

    setSubmitting(true);
    setShowUploadModal(true);
    setOverallProgress(0);

    try {
      const filesToUpload = [
        { field: 'proofOfAddressFile', file: formData.proofOfAddressFile, required: true },
        { field: 'governmentIdFile', file: formData.governmentIdFile, required: true },
        { field: 'financialStatementsFile', file: formData.financialStatementsFile, required: false },
        { field: 'signatureFile', file: formData.signatureFile, required: true },
        { field: 'taxSignatureFile', file: formData.taxSignatureFile, required: true },
        { field: 'amlSignatureFile', file: formData.amlSignatureFile, required: true }
      ];

      const uploadResults = [];
      let completedFiles = 0;
      const totalFiles = filesToUpload.length;

      for (const { field, file, required } of filesToUpload) {
        if (!file) {
          if (required) {
            throw new Error(`${field.replace('File', '')} is required`);
          }
          completedFiles++;
          setOverallProgress(Math.round((completedFiles / totalFiles) * 100));
          continue;
        }

        try {
          const url = await uploadFile(file, field);
          if (!url && required) {
            throw new Error(`Failed to upload required file: ${field}`);
          }
          completedFiles++;
          setOverallProgress(Math.round((completedFiles / totalFiles) * 100));
          uploadResults.push({ field, url: url || null, success: !!url });
        } catch (error) {
          completedFiles++;
          setOverallProgress(Math.round((completedFiles / totalFiles) * 100));
          uploadResults.push({ field, url: null, success: false, error: error.message });
          throw error;
        }
      }

      const fileUrls = uploadResults.reduce((acc, { field, url }) => {
        if (url) {
          acc[`${field}Url`] = url;
        }
        return acc;
      }, {});

      const dataToSave = {
        ...(formData.proofOfAddress && { proofOfAddress: formData.proofOfAddress }),
        ...(formData.governmentIdType && { governmentIdType: formData.governmentIdType }),
        ...(formData.idNumber && { idNumber: formData.idNumber }),
        ...(formData.socialSecurityNumber && { socialSecurityNumber: formData.socialSecurityNumber }),
        ...(formData.sourceOfFunds && { sourceOfFunds: formData.sourceOfFunds }),
        ...(formData.otherSource && { otherSource: formData.otherSource }),
        ...(formData.estimatedNetWorth && { estimatedNetWorth: formData.estimatedNetWorth }),
        ...(formData.fullName && { fullName: formData.fullName }),
        ...(formData.dateOfBirth && { dateOfBirth: formData.dateOfBirth }),
        ...(formData.residentialAddress && { residentialAddress: formData.residentialAddress }),
        ...(formData.city && { city: formData.city }),
        ...(formData.state && { state: formData.state }),
        ...(formData.postalCode && { postalCode: formData.postalCode }),
        ...(formData.isUSCitizen && { isUSCitizen: formData.isUSCitizen }),
        ...(formData.taxResidencyCountries && { taxResidencyCountries: formData.taxResidencyCountries }),
        ...(formData.tin && { tin: formData.tin }),
        ...(formData.noTinReason && { noTinReason: formData.noTinReason }),
        ...(formData.clientName && { clientName: formData.clientName }),
        ...(formData.isPEP && { isPEP: formData.isPEP }),
        ...(formData.highRiskIndustries && { highRiskIndustries: formData.highRiskIndustries }),
        ...(formData.offshoreJurisdictions && { offshoreJurisdictions: formData.offshoreJurisdictions }),
        ...(formData.investigatedForCrimes && { investigatedForCrimes: formData.investigatedForCrimes }),
        ...(formData.explanation && { explanation: formData.explanation }),
        ...(formData.amlDate && { amlDate: formData.amlDate }),
        ...fileUrls,
        completed: true,
        lastUpdated: new Date().toISOString()
      };

      const docRef = doc(db, 'credentials', user.uid);
      await setDoc(docRef, dataToSave, { merge: true });
      
      navigate('/dashboard');
    } catch (error) {
      console.error('Error submitting form:', error);
      alert(`Submission failed: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const isUploading = Object.values(fileUploads).some(upload => upload.status === 'uploading');
  const hasUploadErrors = Object.values(fileUploads).some(upload => upload.status === 'failed');

  if (loading) {
    return <Spinner/>;
  }

  return (
     <div className='py-36'>
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-md">
      <h1 className="text-2xl font-bold text-center mb-6 text-[#000300]">KYC, Tax Residency & AML Declaration</h1>
      
      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex justify-between mb-2">
          <span className={`font-medium ${step >= 1 ? 'text-[#000]' : 'text-gray-500'}`}>Step 1 </span>
          <span className={`font-medium ${step >= 2 ? 'text-[#000]' : 'text-gray-500'}`}>Step 2</span>
          <span className={`font-medium ${step >= 3 ? 'text-[#000]' : 'text-gray-500'}`}>Step 3 </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2.5">
          <div 
            className="bg-green-600 h-2.5 rounded-full" 
            style={{ width: `${(step / 3) * 100}%` }}
          ></div>
        </div>
      </div>

      {/* Upload Progress Modal */}
       {showUploadModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm">
          <div className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-xl shadow-2xl w-96 border border-gray-100">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-gray-800">Please wait...</h3>
              {!isUploading && (
                <button 
                  onClick={() => setShowUploadModal(false)}
                  className="text-green-400 hover:text-gray-500 transition-colors"
                >
                  <FaTimes className="text-lg" />
                </button>
              )}
            </div>
      
      {/* Simplified progress indicator without percentage */}
      <div className="relative flex justify-center mb-6">
        <div className="w-32 h-32 flex items-center justify-center">
          {isUploading ? (
            <div className="relative w-24 h-24">
              <div className="absolute inset-0 border-4 border-gray-100 rounded-full"></div>
              <div 
                className="absolute inset-0 border-4 border-green-600 rounded-full border-t-transparent animate-spin"
                style={{ animationDuration: '1.5s' }}
              ></div>
            </div>
          ) : hasUploadErrors ? (
            <div className="w-24 h-24 flex items-center justify-center rounded-full bg-red-50 text-red-500">
              <FaExclamationTriangle className="text-3xl" />
            </div>
          ) : (
            <div className="w-24 h-24 flex items-center justify-center rounded-full bg-green-50 text-green-500">
              <FaCheckCircle className="text-3xl" />
            </div>
          )}
        </div>
      </div>
      
      {/* Status message */}
      <div className="text-center mb-6">
        {isUploading ? (
          <div className="space-y-2">
            <p className="text-green-500 font-medium">Processing your documents</p>
          </div>
        ) : hasUploadErrors ? (
          <div className="space-y-2">
            <p className="text-red-500 font-medium">Upload completed with errors</p>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-green-500 font-medium">All document submitted successful</p>
          </div>
        )}
      </div>
      
      {/* Progress bar (visual only, no numbers) */}
      <div className="mt-2 mb-6">
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className="h-full bg-green-600 rounded-full transition-all duration-300"
            style={{ width: `${overallProgress}%` }}
          ></div>
        </div>
      </div>
      
      {/* Action button */}
      <div className="mt-4">
        {isUploading ? (
          <button
            disabled
            className="w-full py-3 px-4 bg-gray-700 text-gray-50 rounded-lg font-medium cursor-not-allowed"
          >
            Processing...
          </button>
        ) : (
          <button
            onClick={() => setShowUploadModal(false)}
            className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
              hasUploadErrors 
                ? 'bg-red-100 text-red-600 hover:bg-red-200'
                : 'bg-[#000300] text-white hover:bg-gray-900'
            }`}
          >
            {hasUploadErrors ? 'Review Errors' : 'Continue'}
          </button>
        )}
      </div>
    </div>
  </div>
)}

      <form onSubmit={handleSubmit}>
        {step === 1 && (
          <KYCForm 
            formData={formData} 
            handleChange={handleChange} 
            handleCheckboxChange={handleCheckboxChange} 
          />
        )}
        
        {step === 2 && (
          <TaxResidencyForm 
            formData={formData} 
            handleChange={handleChange} 
          />
        )}
        
        {step === 3 && (
          <AMLForm 
            formData={formData} 
            handleChange={handleChange} 
          />
        )}

        <div className="flex justify-between mt-8 gap-4">
        {step > 1 && (
          <button
            type="button"
            onClick={prevStep}
            disabled={isUploading || submitting}
            className="px-4 py-2 sm:px-6 bg-gray-300 text-gray-700 font-medium rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 flex-1 sm:flex-none"
          >
            Back
          </button>
        )}
        
        {step < 3 ? (
          <button
            type="button"
            onClick={nextStep}
            disabled={isUploading || submitting}
            className="px-4 py-2 sm:px-6 bg-[#000300] text-white font-medium rounded-md hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-700 focus:ring-offset-2 sm:ml-auto disabled:opacity-50 flex-1 sm:flex-none"
          >
            Continue
          </button>
        ) : (
          <button
            type="submit"
            disabled={isUploading || submitting || hasUploadErrors}
            className="px-4 py-2 sm:px-6 bg-[#000300] text-white font-medium rounded-md hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:ring-offset-2 sm:ml-auto disabled:opacity-50 flex-1 sm:flex-none"
          >
            {submitting ? (
              <span className="flex items-center justify-center">
                <FaSpinner className="animate-spin mr-2" />
                <span className="whitespace-nowrap">Submitting...</span>
              </span>
            ) : (
              <span className="whitespace-nowrap">Submit</span>
            )}
          </button>
        )}
            </div>
            </form>
        </div>
    </div>
  );
};

export default MultiStepForm;